'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { ReportsTable } from '@/app/reports/components/ReportsTable';
import {
  getReportPreferences,
  toggleReportPin,
  type ReportPreferences,
} from '@/app/reports/hooks/useReportPreferences';
import { mapCatalogToRows } from '@/app/reports/utils/map-catalog-to-rows';
import { PageHeader } from '@/components/ui/page-header';
import { TableCard } from '@/components/ui/table-card';
import { useTablePage } from '@/hooks/use-table-page';
import { useOrgNavigate } from '@/hooks/use-app-navigate';
import { useReportsCatalog } from '@/lib/api/reports';

import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';

export default withRequiredPermission(ReportsLibraryPage, PERMISSIONS.BILLING_VIEW);

function ReportsLibraryPage() {
  const { t } = useTranslation('reports');
  const { orgSlug, orgRoutes } = useOrgNavigate();
  const accountKey = orgSlug ?? 'default';

  const { searchQuery, setSearchQuery, debouncedSearch, setTableState } = useTablePage({
    initialSorting: [
      { id: 'isPinned', desc: true },
      { id: 'lastViewedAt', desc: true },
    ],
  });

  const [prefs, setPrefs] = React.useState<ReportPreferences>(() => getReportPreferences(accountKey));

  React.useEffect(() => {
    setPrefs(getReportPreferences(accountKey));
  }, [accountKey]);

  const { data: catalog = [], isLoading, isFetching, error, refetch } = useReportsCatalog();

  const rows = React.useMemo(
    () => mapCatalogToRows(catalog, prefs, t, debouncedSearch),
    [catalog, prefs, t, debouncedSearch],
  );

  const handleTogglePin = React.useCallback(
    (reportId: string) => {
      setPrefs(toggleReportPin(accountKey, reportId));
    },
    [accountKey],
  );

  return (
    <div className="flex flex-col space-y-4 sm:space-y-6">
      <PageHeader title={t('library.title')} description={t('library.subtitle')} />

      <TableCard>
        <ReportsTable
          rows={rows}
          loading={isLoading || isFetching}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onTableStateChange={setTableState}
          onTogglePin={handleTogglePin}
          onRefetch={refetch}
          reportDetailsHref={orgRoutes.REPORT_DETAILS}
        />

        {error && (
          <div className="mt-4 rounded border border-red-400 bg-red-100 px-4 py-3 text-red-700">
            {error instanceof Error ? error.message : t('viewer.error')}
          </div>
        )}
      </TableCard>
    </div>
  );
}
